/* eslint-disable @typescript-eslint/ban-ts-comment */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Breadcrumbs, Head, renderWithResolver, formatReviewToSchema } from "@cinch-nx/landing-ui-shared";
import { useStoryblokState } from "@cinch-nx/data-storyblok";
import { Intro, ReviewSections, LatestCars } from "./components";
import { Verdict } from "./components/review-sections/verdict";
import { reviewSectionsData } from "./components/review-sections/review-sections.data";
import { GalleryModal } from "./components/gallery-modal";
import styles from "./car-reviews-v2.module.css";
import { RelatedReviews } from "./components/related-reviews";
import { useVehicleGallery } from "./hooks";
import { useEffect } from "react";
import { CtaPreFooter } from "./components/cta-prefooter/cta-prefooter";
import { ProsAndCons } from "./components/pros-and-cons/pros-and-cons";
import { getDefaultSectionImage, getFormattedGalleryImages, getGalleryImages } from "./helpers";
import { ErrorModal } from "./components/error-modal";
import { removeDuplicatesKeepPlaceholders } from "./utils/remove-duplicates-images/remove-duplicates-images";
import Script from "next/script";
export const placeholderImage = {
    alt: "Image coming soon",
    copyright: "cinch",
    filename: "https://a.storyblok.com/f/132543/400x253/4f1faa5d21/image-coming-soon.png",
    title: "Image coming soon",
    fieldtype: null,
    focus: null,
    name: "Image coming soon"
};
const JsonLdScript = ({ schema , tagId  })=>{
    const schemaToRender = Array.isArray(schema) ? schema[0] : schema;
    return /*#__PURE__*/ _jsx(Script, {
        type: "application/ld+json",
        "data-testid": "json-ld-script",
        id: tagId,
        strategy: "beforeInteractive",
        dangerouslySetInnerHTML: {
            __html: JSON.stringify({
                "@context": "https://schema.org",
                ...schemaToRender
            }, null, 2)
        }
    });
};
export function CarReviewsV2Feature({ story , generatedAt , preview , validationErrors , areVehiclesInStock  }) {
    var ref, ref1, ref2, ref3;
    const { content  } = useStoryblokState({
        story,
        enableBridge: preview
    });
    const IS_MOBILE_APP = "object" !== "undefined" && ((ref = window.navigator) === null || ref === void 0 ? void 0 : ref.userAgent.includes("cinchapp"));
    const { setImageIndexes  } = useVehicleGallery();
    const SEO = (content === null || content === void 0 ? void 0 : (ref1 = content.SEO) === null || ref1 === void 0 ? void 0 : ref1[0]) ?? {};
    const { make , model , verdict , starRating , introText , authorImage , authorName , title , interiorSection , performanceSection , practicalitySection , runningCostsSection , gallery , shouldShowLatestCars , relatedReviews , pros , cons , video  } = content;
    const sections = [
        interiorSection[0],
        performanceSection[0],
        practicalitySection[0],
        runningCostsSection[0]
    ];
    const prosList = pros[0].ProAndConList;
    const consList = cons[0].ProAndConList;
    const titles = reviewSectionsData.map((item)=>item.title);
    const reviewSections = sections.map((section, index)=>({
            ...section,
            title: titles[index]
        }));
    const contentStarRating = Number(starRating);
    const contentIntroText = renderWithResolver(introText);
    const verdictTitle = ((ref2 = verdict[0]) === null || ref2 === void 0 ? void 0 : ref2.title) || "";
    const verdictBody = renderWithResolver((ref3 = verdict[0]) === null || ref3 === void 0 ? void 0 : ref3.body);
    const galleryImages = getGalleryImages(gallery);
    const formattedGalleryImages = getFormattedGalleryImages(galleryImages, placeholderImage);
    const introImage = formattedGalleryImages === null || formattedGalleryImages === void 0 ? void 0 : formattedGalleryImages.at(0);
    const imagesWithDefaultSectionsImages = [
        ...formattedGalleryImages,
        ...sections.map((section)=>getDefaultSectionImage([
                section
            ], placeholderImage))
    ];
    const images = removeDuplicatesKeepPlaceholders(imagesWithDefaultSectionsImages);
    const imageCount = (images === null || images === void 0 ? void 0 : images.length) || 0;
    const formattedSections = {
        interior: interiorSection[0],
        performance: performanceSection[0],
        practicality: practicalitySection[0],
        runningCosts: runningCostsSection[0]
    };
    const latestPublishDate = story === null || story === void 0 ? void 0 : story.published_at;
    useEffect(()=>{
        setImageIndexes(images, formattedSections);
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(JsonLdScript, {
                schema: formatReviewToSchema(content, `${latestPublishDate}`, {
                    pros: prosList,
                    cons: consList
                }),
                tagId: "review"
            }),
            /*#__PURE__*/ _jsx(Head, {
                ...SEO,
                generatedAt: generatedAt,
                fallbackTitle: "Reviews - cinch",
                fallbackDescription: "We're the faff-free car service that puts you in the driving seat. We help you find and buy used cars – quickly and easily. Buy online with home delivery or from an approved dealer."
            }),
            /*#__PURE__*/ _jsxs("article", {
                className: styles.page,
                children: [
                    gallery && /*#__PURE__*/ _jsx(GalleryModal, {
                        images: images
                    }),
                    !IS_MOBILE_APP ? /*#__PURE__*/ _jsx("div", {
                        className: styles.breadcrumbSection,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: styles.breadcrumbWrapper,
                            children: /*#__PURE__*/ _jsx(Breadcrumbs, {
                                breadcrumbs: [
                                    {
                                        text: "Home",
                                        url: "/"
                                    },
                                    {
                                        text: "Reviews",
                                        url: "/reviews"
                                    },
                                    {
                                        text: title,
                                        url: ""
                                    }
                                ]
                            })
                        })
                    }) : null,
                    validationErrors && validationErrors.length > 0 ? /*#__PURE__*/ _jsx(ErrorModal, {
                        validationErrors: validationErrors
                    }) : null,
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.sectionWrapper,
                        children: introImage && latestPublishDate && /*#__PURE__*/ _jsx(Intro, {
                            contentTitle: title,
                            contentStarRating: contentStarRating,
                            contentIntroText: contentIntroText,
                            authorName: authorName,
                            authorImage: authorImage,
                            imageCount: imageCount,
                            introImage: introImage,
                            latestPublishDate: latestPublishDate,
                            video: video
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.prosAndConsWrapper,
                        children: /*#__PURE__*/ _jsx(ProsAndCons, {
                            prosList: prosList,
                            consList: consList,
                            make: make,
                            model: model,
                            areVehiclesInStock: areVehiclesInStock
                        })
                    }),
                    /*#__PURE__*/ _jsx(ReviewSections, {
                        make: make,
                        model: model,
                        reviewSections: reviewSections,
                        imageCount: imageCount,
                        placeholderImage: placeholderImage,
                        areVehiclesInStock: areVehiclesInStock,
                        children: /*#__PURE__*/ _jsx("div", {
                            className: styles.verdictWrapper,
                            children: /*#__PURE__*/ _jsx(Verdict, {
                                title: verdictTitle,
                                body: verdictBody,
                                starRating: contentStarRating,
                                make: make,
                                model: model,
                                reviewSections: reviewSections,
                                areVehiclesInStock: areVehiclesInStock
                            })
                        })
                    }),
                    shouldShowLatestCars && /*#__PURE__*/ _jsx("div", {
                        className: styles.sectionWrapper,
                        children: /*#__PURE__*/ _jsx(LatestCars, {
                            make: make,
                            model: model
                        })
                    }),
                    relatedReviews.length > 0 && /*#__PURE__*/ _jsx("div", {
                        className: styles.sectionWrapper,
                        children: /*#__PURE__*/ _jsx(RelatedReviews, {
                            make: make,
                            model: model,
                            items: relatedReviews
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.sectionWrapper,
                        children: /*#__PURE__*/ _jsx(CtaPreFooter, {})
                    })
                ]
            })
        ]
    });
}
export default CarReviewsV2Feature;
